/**
 * ColorSetting Style02 - Stitch v2: 色溫 only（非彩色）、gold 主題
 * 範圍：attributes.colorTemperatureRange（預設 2700K–6500K）
 */
import { useState, useEffect, useMemo } from 'react'
import type { TraitControlProps } from './types'
import { resolveRangeFromAttributes } from './traitStyleConfig'
import './ColorSettingStyle02.css'


function kelvinToRgb(k: number): string {
  const t = k / 100
  let r: number, g: number, b: number
  if (t <= 66) {
    r = 255
    g = Math.min(255, Math.max(0, 99.4708025861 * Math.log(t) - 161.1195681661))
    b = t <= 19 ? 0 : Math.min(255, Math.max(0, 138.5177312231 * Math.log(t - 10) - 305.0447927307))
  } else {
    r = Math.min(255, Math.max(0, 329.698727446 * Math.pow(t - 60, -0.1332047592)))
    g = Math.min(255, Math.max(0, 288.1221695283 * Math.pow(t - 60, -0.0755148492)))
    b = 255
  }
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`
}

export function ColorSettingStyle02({
  trait,
  state,
  attributes,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const { min: MIN, max: MAX } = useMemo(
    () => resolveRangeFromAttributes(attributes, trait ?? 'ColorSetting', 'colorTemperature'),
    [attributes, trait]
  )
  const presetTemps = useMemo(() => {
    const step = (MAX - MIN) / 4
    return [MIN, Math.round(MIN + step), Math.round(MIN + step * 2), Math.round(MIN + step * 3), MAX]
  }, [MIN, MAX])
  const raw = (state?.colorTemperature ?? state?.color_temp ?? state?.temperature) as number | undefined
  const current = typeof raw === 'number' ? Math.max(MIN, Math.min(MAX, Math.round(raw))) : 4000
  const [temp, setTemp] = useState(current)

  useEffect(() => {
    const v = typeof raw === 'number' ? Math.max(MIN, Math.min(MAX, Math.round(raw))) : 4000
    setTemp(v)
  }, [raw, MIN, MAX])

  const handleSliderChange = (value: number) => {
    const safe = Math.max(MIN, Math.min(MAX, Math.round(value)))
    setTemp(safe)
    void onCommand('setColorTemperature', { temperature: safe })
  }

  const handleSwatchClick = (t: number) => {
    if (disabled) return
    setTemp(t)
    void onCommand('setColorTemperature', { temperature: t })
  }

  return (
    <div className="trait-colorsetting-style02 trait-stitch-v2-glass">
      <div className="trait-colorsetting-style02__header">
        <span className="trait-colorsetting-style02__title">Color Temp</span>
        <span className="material-symbols-outlined trait-colorsetting-style02__icon">wb_sunny</span>
      </div>
      <div
        className="trait-colorsetting-style02__slider-wrap"
        style={{ '--thumb-color': kelvinToRgb(temp) } as React.CSSProperties}
      >
        <input
          type="range"
          min={MIN}
          max={MAX}
          value={temp}
          onChange={(e) => setTemp(Number(e.target.value))}
          onMouseUp={(e) => handleSliderChange(Number((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => handleSliderChange(Number((e.target as HTMLInputElement).value))}
          className="trait-colorsetting-style02__slider"
          disabled={disabled}
        />
      </div>
      <div className="trait-colorsetting-style02__swatches">
        {presetTemps.map((t) => (
          <button
            key={t}
            type="button"
            className={`trait-colorsetting-style02__swatch ${Math.abs(temp - t) < 200 ? 'selected' : ''}`}
            style={{ backgroundColor: kelvinToRgb(t) }}
            onClick={() => handleSwatchClick(t)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  )
}
